/**
 * One version's items, 100 per page. A row opens the item in full (`?item=`).
 * Output shows when some item records one; Trace when some item was pulled from traces.
 */

import { ExternalLink } from "lucide-react";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import { QueryState } from "@/components/ui/states";
import { inputEntries, preview } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { DatasetItemPanel } from "./dataset-item-panel";
import { useDatasetItems } from "./hooks";
import type { DatasetItem } from "./types";

const TRACE: Column<DatasetItem> = {
  header: "Trace",
  isolated: true,
  cell: (it) =>
    it.trace_url ? (
      <a
        className="link inline-flex"
        href={it.trace_url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open trace of item ${it.position + 1}`}
      >
        <ExternalLink size={13} strokeWidth={2} aria-hidden />
      </a>
    ) : (
      <span className="text-dim">—</span>
    ),
};

const COLUMNS = (open: (id: string) => void, rows: DatasetItem[]): Column<DatasetItem>[] => [
  {
    header: "#",
    className: "font-mono text-muted",
    cell: (it) => (
      <button
        type="button"
        aria-label={`Open item ${it.position + 1}`}
        onClick={(ev) => (ev.stopPropagation(), open(it.id))}
      >
        {it.position + 1}
      </button>
    ),
  },
  {
    header: "input",
    className: "max-w-[360px]",
    cell: (it) => (
      <span className="clamp-2 font-mono">{preview(Object.fromEntries(inputEntries(it.input)))}</span>
    ),
  },
  {
    header: "expected_output",
    className: "max-w-[300px] text-muted",
    cell: (it) => <span className="clamp-2">{preview(it.expected_output)}</span>,
  },
  ...(rows.some((it) => it.output !== null && it.output !== undefined)
    ? [
        {
          header: "output",
          className: "max-w-[260px] text-muted",
          cell: (it: DatasetItem) => <span className="clamp-2">{preview(it.output)}</span>,
        },
      ]
    : []),
  {
    header: "metadata",
    className: "max-w-[200px] text-muted",
    cell: (it) => <span className="clamp-2">{preview(it.metadata, 80)}</span>,
  },
  ...(rows.some((it) => it.trace_url) ? [TRACE] : []),
];

export function DatasetItems({ datasetId, version }: { datasetId: string; version: number }) {
  const url = useUrlState();
  const page = Number(url.get("page")) || 1;
  const items = useDatasetItems(datasetId, version, page);
  const total = items.data?.total ?? 0;
  const size = items.data?.page_size ?? 100;
  const itemId = url.get("item");
  const open = (id: string) => url.set({ item: id });
  const rows = items.data?.items ?? [];

  return (
    <Box>
      <QueryState query={items} what="items">
        <DataTable
          columns={COLUMNS(open, rows)}
          rows={rows}
          onRowClick={(it) => open(it.id)}
          className="tbl text-xs"
        />
        <div className="flex items-center gap-2 border-t border-line-soft bg-well px-4 py-2.5 text-xs text-muted">
          <span className="flex-1">
            {total
              ? `Showing ${(page - 1) * size + 1}–${Math.min(page * size, total)} of ${total}`
              : "No items"}
          </span>
          <Button
            size="sm"
            disabled={page <= 1}
            onClick={() => url.set({ page: page > 2 ? page - 1 : null, item: null })}
          >
            Previous
          </Button>
          <Button
            size="sm"
            disabled={page * size >= total}
            onClick={() => url.set({ page: page + 1, item: null })}
          >
            Next
          </Button>
        </div>
      </QueryState>
      {itemId && items.data && (
        <DatasetItemPanel
          items={items.data.items}
          itemId={itemId}
          onOpen={open}
          onClose={() => url.set({ item: null })}
        />
      )}
    </Box>
  );
}
